import React, { useState, useEffect } from 'react';
import { TODOS_HOSPITAIS, hospitaisMaisProximosTodos, Hospital } from './hospitais-index';

type HospitalComDist = Hospital & { distanciaKm: number };

function abrirNavegacao(hospital: Hospital) {
  // Usa endereço + cidade como destino — muito mais preciso que coordenadas
  const enderecoCompleto = `${hospital.nome}, ${hospital.endereco}, ${hospital.cidade}, Brasil`;
  const enderecoQuery = encodeURIComponent(enderecoCompleto);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  if (isIOS) {
    // Apple Maps — busca por nome + endereço
    window.open(`maps://maps.apple.com/?q=${enderecoQuery}&dirflg=d`, '_blank');
  } else if (isAndroid) {
    // Android — abre app de mapas padrão do usuário com busca por endereço
    window.open(`geo:0,0?q=${enderecoQuery}`, '_blank');
  } else {
    // Desktop — Google Maps com busca textual
    window.open(`https://www.google.com/maps/search/?api=1&query=${enderecoQuery}`, '_blank');
  }
}

// Geocode de CEP via ViaCEP + nominatim
async function geocodeCEP(cep: string): Promise<{ lat: number; lng: number; cidade: string } | null> {
  try {
    const clean = cep.replace(/\D/g, '');
    if (clean.length !== 8) return null;
    const res = await fetch(`https://viacep.com.br/ws/${clean}/json/`);
    const data = await res.json();
    if (data.erro) return null;
    const addr = `${data.logradouro}, ${data.localidade}, ${data.uf}, Brasil`;
    const geo = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(addr)}&format=json&limit=1`);
    const geoData = await geo.json();
    if (!geoData[0]) return null;
    return { lat: parseFloat(geoData[0].lat), lng: parseFloat(geoData[0].lon), cidade: `${data.localidade}, ${data.uf}` };
  } catch { return null; }
}

export default function HospitalScreen({ setScreen }: { setScreen: (s: any) => void }) {
  const [hospitais, setHospitais] = useState<HospitalComDist[]>([]);
  const [locLabel, setLocLabel] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'ok' | 'error'>('idle');
  const [cep, setCep] = useState('');
  const [cepLoading, setCepLoading] = useState(false);

  useEffect(() => { detectarLocalizacao(); }, []);

  const detectarLocalizacao = () => {
    setStatus('loading');
    setLocLabel('');
    if (!navigator.geolocation) {
      setStatus('error');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      pos => {
        const { latitude, longitude } = pos.coords;
        const lista = hospitaisMaisProximosTodos(latitude, longitude, 5);
        setHospitais(lista);
        setLocLabel('sua localização atual');
        setStatus('ok');
      },
      () => setStatus('error'),
      { timeout: 8000 }
    );
  };

  const buscarCEP = async () => {
    if (!cep.trim()) return;
    setCepLoading(true);
    const result = await geocodeCEP(cep);
    if (result) {
      const lista = hospitaisMaisProximosTodos(result.lat, result.lng, 5);
      setHospitais(lista);
      setLocLabel(result.cidade);
      setStatus('ok');
    } else {
      alert('CEP não encontrado. Verifique e tente novamente.');
    }
    setCepLoading(false);
  };

  return (
    <div className="screen">
      <div className="sub-header">
        <button className="back-btn" onClick={() => setScreen('home')}>←</button>
        <div className="sub-header-text">
          <h2>🏥 Hospital com soro</h2>
          <p>Antiescorpiônico disponível — SP</p>
        </div>
      </div>
      <div className="scroll-content">
        <div className="search-bar">
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text" placeholder="CEP (ex: 01310-100)..."
              value={cep} onChange={e => setCep(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && buscarCEP()}
              style={{ flex: 1 }}
            />
            <button
              onClick={buscarCEP}
              disabled={cepLoading}
              style={{
                padding: '0 16px', background: 'var(--red)', color: '#fff',
                border: 'none', borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font)', fontWeight: 600, fontSize: 13,
                cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0
              }}>
              {cepLoading ? '...' : 'Buscar'}
            </button>
          </div>
          <button
            onClick={detectarLocalizacao}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              marginTop: 8, padding: '8px 12px', background: 'transparent',
              border: '1.5px solid var(--gray-200)', borderRadius: 'var(--radius-sm)',
              fontFamily: 'var(--font)', fontSize: 13, color: 'var(--gray-600)',
              cursor: 'pointer', width: '100%', justifyContent: 'center'
            }}>
            📍 Usar minha localização atual (GPS)
          </button>
        </div>

        {status === 'loading' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '1rem 1.25rem' }}>
            <div className="spinner" style={{ borderTopColor: 'var(--red)', borderColor: 'var(--gray-200)' }} />
            <span style={{ fontSize: 14, color: 'var(--gray-600)' }}>Detectando localização...</span>
          </div>
        )}

        {status === 'error' && (
          <div className="alert-box warning">
            <span className="alert-icon">⚠️</span>
            <p>Não foi possível detectar sua localização. Digite o CEP acima para buscar hospitais próximos.</p>
          </div>
        )}

        {status === 'ok' && locLabel && (
          <div className="alert-box info">
            <span className="alert-icon">📍</span>
            <p>Mostrando hospitais próximos a <strong>{locLabel}</strong>. Confirme disponibilidade do soro por telefone antes de sair.</p>
          </div>
        )}

        {status === 'ok' && hospitais.map((h, i) => (
          <div key={h.id} className={`hospital-card ${i === 0 ? 'primary' : ''}`}>
            <div className="hosp-tag">
              {i === 0 ? '⭐ Mais próximo' : `${i + 1}º mais próximo`}
              {' · '}{h.distanciaKm < 1
                ? `${Math.round(h.distanciaKm * 1000)} m`
                : `${h.distanciaKm.toFixed(1)} km`}
              {' · '}{h.regiao}
            </div>
            <div className="hosp-name">{h.nome}</div>
            <div className="hosp-addr">{h.endereco}</div>
            <div className="hosp-addr" style={{ marginTop: 2 }}>📍 {h.cidade}</div>
            <div className="hosp-addr" style={{ marginTop: 2 }}>📞 {h.telefone}</div>
            {h.municipiosAtendidos && (
              <div className="hosp-addr" style={{ marginTop: 4, fontSize: 11 }}>
                Atende: {h.municipiosAtendidos}
              </div>
            )}
            <div className="hosp-badges" style={{ marginTop: 8 }}>
              <span className="badge badge-green">✓ Soro disponível</span>
              <span className="badge badge-blue">24h SUS</span>
              {h.soros.map(s => (
                <span key={s} className="badge badge-amber">{s}</span>
              ))}
            </div>
            <button className="route-btn" onClick={() => abrirNavegacao(h)}>
              📍 Abrir rota no maps
            </button>
            <button className="route-btn secondary" style={{ marginTop: 6 }}
              onClick={() => window.open(`tel:${h.telefone.replace(/\D/g, '')}`)}>
              📞 Ligar agora
            </button>
          </div>
        ))}

        <div className="info-card" style={{ marginTop: 4 }}>
          <h4>ℹ️ Sobre os dados</h4>
          <p>
            Base com {TODOS_HOSPITAIS.length} PESAs (Pontos Estratégicos de Soro Antiveneno) do estado de SP.
            Fonte: Secretaria de Estado da Saúde de SP / COSEMSSP, atualizado jun/2023.
            Nem todo PS tem soro — confirme por telefone antes de ir.
          </p>
        </div>
        <div style={{ padding: '0 1.25rem 0.5rem' }}>
          <a
            href="https://nies.saude.sp.gov.br/ses/publico/soro"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 13, color: 'var(--blue)', textDecoration: 'underline' }}>
            Ver mapa oficial da Secretaria de Saúde SP →
          </a>
        </div>
        <div className="pb-safe" />
      </div>
      <nav className="bottom-nav">
        {[
          { id: 'home', icon: '🏠', label: 'Início' },
          { id: 'primeiros-socorros', icon: '🚨', label: 'Socorros' },
          { id: 'hospital', icon: '🏥', label: 'Hospitais' },
          { id: 'camera', icon: '📷', label: 'Fotografar' },
          { id: 'info', icon: '📚', label: 'Prevenção' },
        ].map(t => (
          <button key={t.id} className={`nav-btn ${t.id === 'hospital' ? 'active' : ''}`}
            onClick={() => setScreen(t.id)}>
            <span className="nav-icon">{t.icon}</span>
            <span className="nav-label">{t.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
